import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { useTranslate } from 'react-admin';
import {
  Button,
  IconButton,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Typography,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { Dashboard, DashboardWidget, getWidgetType } from 'types/dashboard.ui';
import { dashboardAPI } from '../../api/dashboard';
import ChartWidget from './widgets/chart';
import TableWidget from './widgets/table';
import GaugeWidget from './widgets/gauge';
import TextWidget from './widgets/text';
import SchemaWidget from './widgets/schema';
import MapWidget from './widgets/map';
import WidgetCreationWizard from './widget.creation';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import './dashboard.css';

const ResponsiveGridLayout = WidthProvider(Responsive);

interface DashboardViewerProps {
  dashboardId: string;
  editMode?: boolean;
  onEditModeChange?: (editMode: boolean) => void;
  onEditWidget?: (widget: DashboardWidget) => void;
}

const DashboardViewer: React.FC<DashboardViewerProps> = ({
  dashboardId,
  editMode = false,
  onEditModeChange,
  onEditWidget,
}: DashboardViewerProps) => {
  const translate = useTranslate();
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [layouts, setLayouts] = useState<{[key: string]: any}>({});
  const [selectedWidget, setSelectedWidget] = useState<DashboardWidget | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [widgetCreationOpen, setWidgetCreationOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dashboardRef = useRef<HTMLDivElement | null>(null);

  // Обработчик изменения состояния fullscreen
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      dashboardRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }, []);

  const loadDashboard = useCallback(() => {
    setLoading(true);
    setError('');

    dashboardAPI.getDashboard(dashboardId)
      .then((data) => {
        setDashboard(data);
        
        // Инициализация layouts из сохраненных позиций виджетов
        const initialLayouts = data.widgets.reduce((acc: any, widget: DashboardWidget) => {
          const baseLayout = {
            i: widget.id.toString(),
            x: widget.position_x || 0,
            y: widget.position_y || 0,
            w: widget.width || 6,
            h: widget.height || 6,
            minW: 2, // Минимальная ширина 2 колонки
            minH: 2, // Минимальная высота 2 строки
          };
          
          // Адаптируем ширину для разных breakpoints
          const layouts = {
            lg: { ...baseLayout },
            md: { ...baseLayout },
            sm: { ...baseLayout, w: Math.min(baseLayout.w, 12) },
            xs: { ...baseLayout, w: Math.min(baseLayout.w, 12), x: 0 },
            xxs: { ...baseLayout, w: 12, x: 0 }
          };
          
          Object.keys(layouts).forEach(breakpoint => {
            if (!acc[breakpoint]) acc[breakpoint] = [];
            acc[breakpoint].push(layouts[breakpoint as keyof typeof layouts]);
          });
          
          return acc;
        }, { lg: [], md: [], sm: [], xs: [], xxs: [] });
        
        setLayouts(initialLayouts);
      })
      .catch((err) => {
        console.error('Error loading dashboard:', err);
        setError(err instanceof Error ? err.message : translate('resources.dashboards.messages.unknown_error'));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [dashboardId, translate]);

  useEffect(() => {
    if (dashboardId) {
      loadDashboard();
    }
    
    // Cleanup функция для отмены таймера при размонтировании
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [dashboardId, loadDashboard]);

  const handleLayoutChange = useCallback((layout: any[], layouts: {[key: string]: any}) => {
    setLayouts(layouts);
    
    if (editMode && dashboard) {
      // Отменяем предыдущий таймер, если он есть
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      
      // Устанавливаем новый таймер для сохранения (debounce)
      saveTimeoutRef.current = setTimeout(() => {
        // Собираем все обновления позиций виджетов
        const widgetPositions = layout.map((layoutItem) => {
          const widget = dashboard.widgets.find(w => w.id.toString() === layoutItem.i);
          if (!widget) {
            console.warn('Widget not found for layout item:', layoutItem.i);
            return null;
          }
          
          // Проверяем, действительно ли позиция изменилась
          const hasChanged = 
            widget.position_x !== layoutItem.x ||
            widget.position_y !== layoutItem.y ||
            widget.width !== layoutItem.w ||
            widget.height !== layoutItem.h;
          
          return {
            id: widget.id.toString(),
            position_x: layoutItem.x,
            position_y: layoutItem.y,
            width: layoutItem.w,
            height: layoutItem.h,
          };
        }).filter(p => p !== null) as Array<{
          id: string;
          position_x: number;
          position_y: number;
          width: number;
          height: number;
        }>;
        
        // Отправляем все обновления одним запросом
        if (widgetPositions.length > 0) {
          dashboardAPI.updateWidgetsPositions(dashboardId, widgetPositions)
            .then(() => {
              // Перезагружаем дашборд с бэкенда чтобы получить актуальные данные
              loadDashboard();
            })
            .catch(err => {
              console.error('Error saving widget positions:', err);
            });
        }
      }, 500); // Задержка 500ms для debounce
    }
  }, [editMode, dashboard, dashboardId, loadDashboard]);

  const handleBreakpointChange = useCallback((_newBreakpoint: string) => {
    // Можно использовать для адаптивности в будущем
  }, []);

  const handleDeleteWidget = (widget: DashboardWidget) => {
    if (!dashboard) return;

    const updatedWidgets = dashboard.widgets.filter((w: DashboardWidget) => w.id !== widget.id);
    
    dashboardAPI.updateDashboard(dashboardId, {
      widgets: updatedWidgets,
    })
      .then(() => {
        const updatedDashboard = { ...dashboard, widgets: updatedWidgets };
        setDashboard(updatedDashboard);
        
        // Обновляем layouts
        const updatedLayouts = Object.keys(layouts).reduce((acc: any, breakpoint) => {
          acc[breakpoint] = layouts[breakpoint].filter((item: any) => item.i !== widget.id.toString());
          return acc;
        }, {});
        setLayouts(updatedLayouts);
        
        setDeleteDialogOpen(false);
        setSelectedWidget(null);
      })
      .catch((err) => {
        console.error('Error deleting widget:', err);
      });
  };

  const handleDeleteWidgetClick = (widget: DashboardWidget) => {
    setSelectedWidget(widget);
    setDeleteDialogOpen(true);
  };

  const renderWidget = useCallback((widget: DashboardWidget) => {
    const widgetType = getWidgetType(widget);
    
    const commonProps = {
      widget,
      editMode,
      onEdit: editMode && onEditWidget ? () => onEditWidget(widget) : undefined,
      onDelete: editMode ? () => handleDeleteWidgetClick(widget) : undefined,
    };

    let widgetContent;
    switch (widgetType) {
      case 'chart':
        widgetContent = <ChartWidget {...commonProps} />;
        break;
      case 'table':
        widgetContent = <TableWidget {...commonProps} />;
        break;
      case 'gauge':
        widgetContent = <GaugeWidget {...commonProps} />;
        break;
      case 'text':
        widgetContent = <TextWidget {...commonProps} />;
        break;
      case 'schema':
        widgetContent = <SchemaWidget {...commonProps} />;
        break;
      case 'map':
        widgetContent = <MapWidget {...commonProps} />;
        break;
      default:
        console.warn('Unknown widget type:', widgetType, 'for widget:', widget);
        widgetContent = (
          <div style={{ 
            border: '2px dashed red', 
            padding: '20px', 
            background: '#ffe6e6',
            height: '100%'
          }}>
            <h3>{translate('resources.dashboards.messages.unknown_widget_type', { type: widgetType })}</h3>
            <p>Widget ID: {widget.id}</p>
            <p>Widget Type ID: {widget.widget_type_id}</p>
          </div>
        );
    }

    // Важно: React Grid Layout требует, чтобы key элемента совпадал с i в layout
    return (
      <div key={widget.id.toString()} style={{ width: '100%', height: '100%' }}>
        {widgetContent}
      </div>
    );
  }, [editMode, onEditWidget, translate]);

  if (loading) {
    return (
      <Box 
        sx={{ 
          height: '100%',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default'
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.primary">
            {translate('resources.dashboards.messages.loading')}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error || !dashboard) {
    return (
      <Box 
        sx={{ 
          height: '100%',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default'
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>
            {translate('resources.dashboards.messages.error_loading')}
          </Typography>
          <Typography variant="body2">
            {error || translate('resources.dashboards.messages.not_found')}
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box 
      ref={dashboardRef}
      sx={{ 
        width: '100%', 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        bgcolor: isFullscreen ? 'background.default' : 'transparent',
        position: 'relative'
      }}
    >
      {/* Fullscreen button - в режиме просмотра */}
      {!editMode && (
        <IconButton
          size="small"
          onClick={toggleFullscreen}
          title={translate('internal.fullscreen')}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1000,
            bgcolor: 'background.paper',
            boxShadow: 1,
            '&:hover': {
              bgcolor: 'action.hover'
            }
          }}
        >
          {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
        </IconButton>
      )}

      {/* Dashboard Header - только в режиме редактирования */}
      {editMode && (
        <Box 
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
            bgcolor: 'background.paper'
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 600, mb: 0.5 }}>
              {dashboard.name}
            </Typography>
            {dashboard.description && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {dashboard.description}
              </Typography>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              size="medium"
              startIcon={<AddIcon />}
              onClick={() => setWidgetCreationOpen(true)}
              sx={{ textTransform: 'none' }}
            >
              {translate('resources.dashboards.actions.add_widget')}
            </Button>
          </Box>
        </Box>
      )}

      {/* Widgets Grid */}
      <Box sx={{ flex: 1, width: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {dashboard?.widgets?.length === 0 ? (
          <Box 
            sx={{ 
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h3" sx={{ mb: 2, opacity: 0.3 }}>
                📊
              </Typography>
              <Typography variant="h6" sx={{ color: 'text.primary', mb: 1 }}>
                {translate('resources.dashboards.messages.empty_title')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                {translate('resources.dashboards.messages.empty_subtitle')}
              </Typography>
              {editMode && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => setWidgetCreationOpen(true)}
                  sx={{ textTransform: 'none' }}
                >
                  {translate('resources.dashboards.actions.add_first_widget')}
                </Button>
              )}
            </Box>
          </Box>
        ) : (
          <Box sx={{ flex: 1, width: '100%', p: 2, boxSizing: 'border-box', overflow: 'auto' }}>
            <ResponsiveGridLayout
              className="layout"
              layouts={layouts}
              onLayoutChange={handleLayoutChange}
              onBreakpointChange={handleBreakpointChange}
              breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
              cols={{ lg: 12, md: 12, sm: 12, xs: 12, xxs: 12 }}
              rowHeight={80}
              isDraggable={editMode}
              isResizable={editMode}
              compactType={null}
              preventCollision={false}
              margin={[16, 16]}
              containerPadding={[16, 16]}
              autoSize={false}
              draggableHandle=".drag-handle"
              draggableCancel=".react-grid-item-no-drag"
              useCSSTransforms={true}
            >
              {dashboard.widgets.map(renderWidget)}
            </ResponsiveGridLayout>
          </Box>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {translate('resources.dashboards.messages.delete_confirm_title')}
          <IconButton onClick={() => setDeleteDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {translate('resources.dashboards.messages.delete_confirm_message', { title: selectedWidget?.title })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ gap: 1, p: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setDeleteDialogOpen(false)}
          >
            {translate('resources.dashboards.messages.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => selectedWidget && handleDeleteWidget(selectedWidget)}
          >
            {translate('resources.dashboards.messages.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Widget Creation Wizard */}
      <WidgetCreationWizard
        open={widgetCreationOpen}
        onClose={() => setWidgetCreationOpen(false)}
        onSuccess={(newWidget) => {
          // Добавляем новый виджет к дашборду
          if (dashboard) {
            const updatedDashboard = {
              ...dashboard,
              widgets: [...dashboard.widgets, newWidget]
            };
            setDashboard(updatedDashboard);
          }
          setWidgetCreationOpen(false);
          loadDashboard(); // Перезагружаем дашборд для получения актуальных данных
        }}
        dashboardId={dashboardId}
      />
    </Box>
  );
};

export default DashboardViewer;